import {
  Input,
  Spin,
  Tag as TagAntd,
  Space,
  Button,
  Typography,
  Switch,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import React, { useEffect, useState } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';

import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { createNewTag, fetchAllTags, removeAllTags } from 'src/slices/tag';
import {
  fetchCurrentTeamSettings,
  createNewTeam,
  updateTeamSettings,
} from 'src/slices/auth';
import { useTeamsFx } from 'src/utils/useTeamsFx';
import { tagsSelector } from '../store';
import { useGraph } from '../utils/useGraph';
import { openNotificationWithlogo } from './AddArticle';

const { Title } = Typography;

const useStyles = createUseStyles({
  tagsLayout: {
    padding: '20px',
  },
  loading: {
    fontSize: 24,
    '& ': {
      marginTop: '10px',
    },
  },
  checkableTag: {
    fontSize: 'medium',
    padding: '2px 5px !important',
  },
  addTagLayout: {
    marginTop: '20px',
    display: 'flex',
    width: '50vw',
  },
  addTag: {
    marginRight: '10px',
  },
  channelSelectionLayout: {
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
  },
  channelRow: {
    display: 'flex',
    margin: '4px',
  },
  switch: {
    marginRight: '8px',
  },
});

const Configuration: React.VFC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { context } = useTeamsFx();

  const team = useAppSelector((state) => state.auth.team);
  const { tags, isTagsLoading } = useAppSelector((state) => ({
    tags: tagsSelector.selectAll(state),
    isTagsLoading: state.tag.isLoading,
  }));

  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [newTag, setNewTag] = useState<string>('');

  const [loading, setLoading] = useState<number>(0);

  const [loadingButton, setLoadingButton] = useState<boolean>(false);

  const channels = useGraph(
    async (graph) => {
      try {
        if (context?.groupId) {
          return (await graph.api(`/teams/${context?.groupId}/channels`).get())
            ?.value;
        }
      } catch (error) {
        console.log(error);
      }
    },
    {
      auto: false,
    },
  );

  useEffect(() => {
    if (context?.groupId) {
      channels.reload();
    }
  }, [context?.groupId]);

  useEffect(() => {
    // Initialize the Microsoft Teams SDK
    microsoftTeams.initialize();
    microsoftTeams.settings.setValidityState(true);

    return () => {
      dispatch(removeAllTags());
    };
  }, []);

  useEffect(() => {
    /**
     * When the user clicks "Save", save the url for your configured tab.
     * This allows for the addition of query string parameters based on
     * the settings selected by the user.
     */
    microsoftTeams.settings.registerOnSaveHandler(async (saveEvent) => {
      const baseUrl = `https://${window.location.hostname}:${window.location.port}`;
      microsoftTeams.settings.setSettings({
        suggestedDisplayName: 'Stample',
        entityId: 'Test',
        contentUrl: baseUrl + '/library',
        websiteUrl: baseUrl + '/library',
      });

      /* Save */
      try {
        if (team === null) {
          await dispatch(
            createNewTeam({
              teamId: context?.teamId || '',
              groupId: context?.groupId || '',
              name: context?.teamName,
              enabledTeamsChannelsForBot: selectedChannels,
            }),
          ).unwrap();
        } else {
          await dispatch(
            updateTeamSettings({
              teamId: context?.teamId || '',
              enabledTeamsChannelsForBot: selectedChannels,
            }),
          ).unwrap();
        }
        openNotificationWithlogo('success', t('success'), '');
        saveEvent.notifySuccess();
      } catch (error: any) {
        openNotificationWithlogo(
          'error',
          t('error'),
          t(`errors.${error?.message}`),
        );
        saveEvent.notifyFailure(error.message);
      }
    });
  }, [team, selectedChannels]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (context?.teamId) {
        try {
          await dispatch(fetchAllTags(context.teamId));
          await dispatch(fetchCurrentTeamSettings(context.teamId)).unwrap();
        } catch (error) {
          console.log(error);
          setLoading((loading) => loading + 1);
        }
      } else {
        setLoading((loading) => loading + 1);
      }
    };

    fetchSettings();
  }, [context?.teamId]);

  useEffect(() => {
    if (team) {
      setSelectedChannels(team.enabledTeamsChannelsForBot);
      setLoading((loading) => loading + 1);
    }
  }, [team]);

  const createTag = () => {
    const dispatchNewTag = async () => {
      setLoadingButton(true);
      if (team === null) {
        await dispatch(
          createNewTeam({
            teamId: context?.teamId || '',
            groupId: context?.groupId || '',
            name: context?.teamName,
            enabledTeamsChannelsForBot: selectedChannels,
          }),
        ).unwrap();
      }
      await dispatch(
        createNewTag({ microsoftTeamId: context?.teamId || '', name: newTag }),
      );
      setNewTag('');
      setLoadingButton(false);
    };

    dispatchNewTag();
  };

  const antIcon = <LoadingOutlined spin />;

  return (
    <div className={classes.tagsLayout}>
      {isTagsLoading && (
        <Spin
          indicator={antIcon}
          className={classes.loading}
          tip={t('tags.loading')}
        />
      )}
      {loading > 1 && (
        <div className="configLayout">
          <Title level={5}>{t('tags.selectTags')}</Title>
          <Space size={[8, 10]} wrap className="tagsList">
            {tags.map((tag) => (
              <TagAntd
                key={tag._id}
                className={[classes.checkableTag, 'ant-tag-processing'].join(
                  ' ',
                )}
              >
                {`#${tag.name}`}
              </TagAntd>
            ))}
          </Space>
          <div className={classes.addTagLayout}>
            <Input
              placeholder={t('tags.addTag')}
              allowClear
              className={classes.addTag}
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
            />
            <Button type="primary" onClick={createTag} loading={loadingButton}>
              {t('common.addBtn')}
            </Button>
          </div>

          <div className={classes.channelSelectionLayout}>
            <Title level={5}>{t('tabConfiguration.channelSelection')}</Title>

            {!channels.loading &&
              channels.data &&
              channels.data.map((channel: any) => (
                <div key={channel.id} className={classes.channelRow}>
                  <Switch
                    className={classes.switch}
                    checked={selectedChannels.includes(channel.id)}
                    onChange={(value) =>
                      setSelectedChannels(
                        value
                          ? [...selectedChannels, channel.id]
                          : selectedChannels.filter(
                              (selectedChannel) =>
                                selectedChannel !== channel.id,
                            ),
                      )
                    }
                  />{' '}
                  {channel.displayName}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Configuration;
